// v4 (spec-v4-structure-and-stage.md sections 3.4 and 9, ticket V4-R2): the References fold's pure side. The api
// (V4-R1) picks the pictures; the web shows them, groups what else could go in, works out the PUT body for each tap
// (swap, pin, remove, add, back to automatic), the budget line, the warnings as Notices and whether the picks have
// changed since the frame was made ("Redo with these · 8").
//
// No imports from api.ts (api.ts imports the normalisers from here). The types mirror core/src/references.ts.
import { DEFAULT_LIMITS } from "./library";
import type { NoticeData } from "./nextstep";
import type { RefCandidate, RefKey, RefOverrides, RefRole, RefSheetPart, RefUse, RefWarning, ReferencesView, SetReferencesBody } from "./types";

type Json = Record<string, unknown>;
const isObj = (v: unknown): v is Json => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown) => (typeof v === "string" ? v : v == null ? "" : String(v));
const num = (v: unknown, d = 0) => (typeof v === "number" && Number.isFinite(v) ? v : d);
const keys = (v: unknown) => (Array.isArray(v) ? v.filter((k): k is string => typeof k === "string") : []);

const ROLES: readonly RefRole[] = ["character", "product", "location", "prop", "photo", "user"];
const SHEETS: readonly RefSheetPart[] = ["turnaround", "expressions", "poses"];
/** The sheet's panels in the viewer's order (core sheets.ts ANGLE_PANELS, EXPRESSION_PANELS, POSE_PANELS). */
export const PANEL_ORDER = ["front", "three_quarter", "side", "back", "neutral", "happy", "sad", "angry", "surprised", "thinking", "standing", "walking", "sitting", "pointing"] as const;

// ------------------------------------------------------------------ normalise (GET/PUT .../references)

export function normRefUse(o: Json): RefUse {
  const role = ROLES.includes(o.role as RefRole) ? (o.role as RefRole) : "user";
  const sheet = SHEETS.includes(o.sheet as RefSheetPart) ? (o.sheet as RefSheetPart) : undefined;
  return {
    assetId: str(o.assetId),
    role,
    ...(o.characterId ? { characterId: str(o.characterId) } : {}),
    ...(typeof o.versionN === "number" ? { versionN: o.versionN } : {}),
    ...(o.panel ? { panel: str(o.panel) } : {}),
    ...(sheet ? { sheet } : {}),
    label: str(o.label),
    reason: str(o.reason),
    ...(o.pinned === true ? { pinned: true } : {}),
  };
}

function normCandidate(o: Json): RefCandidate & { url: string | null; thumbUrl: string | null } {
  const { reason: _r, pinned: _p, ...rest } = normRefUse(o);
  const r = { ...rest, key: "" };
  return { ...r, key: str(o.key) || refKey(r), url: str(o.url) || null, thumbUrl: str(o.thumbUrl) || null };
}

function normWarning(o: Json): RefWarning | null {
  const code = o.code === "TOO_MANY_CHARACTERS" || o.code === "MISSING_PANEL" || o.code === "NO_REFS_MODEL" ? o.code : null;
  if (!code) return null;
  const s = isObj(o.suggest) ? o.suggest : null;
  return {
    code,
    text: str(o.text),
    ...(typeof o.fits === "number" ? { fits: o.fits } : {}),
    ...(s && s.modelId ? { suggest: { modelId: str(s.modelId), short: str(s.short) || str(s.modelId), maxRefs: num(s.maxRefs), credits: num(s.credits) } } : {}),
  };
}

export function normReferences(v: unknown): ReferencesView {
  const o = isObj(v) ? v : {};
  const b = isObj(o.budget) ? o.budget : {};
  const ov = isObj(o.overrides) ? o.overrides : {};
  return {
    sceneId: str(o.sceneId),
    picked: (Array.isArray(o.picked) ? o.picked : []).filter(isObj).map((x) => ({ ...normRefUse(x), url: str(x.url) || null, thumbUrl: str(x.thumbUrl) || null })),
    budget: { max: num(b.max), model: str(b.model), modelShort: str(b.modelShort) || str(b.model), ...(typeof b.maxCharacters === "number" && b.maxCharacters > 0 ? { maxCharacters: b.maxCharacters } : {}) },
    warnings: (Array.isArray(o.warnings) ? o.warnings : []).filter(isObj).map(normWarning).filter((w): w is RefWarning => !!w),
    candidates: (Array.isArray(o.candidates) ? o.candidates : []).filter(isObj).map(normCandidate),
    overrides: { pins: keys(ov.pins), removed: keys(ov.removed) },
    promptNote: str(o.promptNote),
  };
}

// ------------------------------------------------------------------ keys

/** core refKeyOf: `char:<id>:<panel | part | turnaround>` for a sheet picture, else `asset:<assetId>`. */
export function refKey(r: Pick<RefUse, "assetId" | "characterId" | "panel" | "sheet">): RefKey {
  return r.characterId ? `char:${r.characterId}:${r.panel ?? r.sheet ?? "turnaround"}` : `asset:${r.assetId}`;
}
export const pickedKeys = (view: Pick<ReferencesView, "picked">): RefKey[] => view.picked.map(refKey);

/** "Pip, side view" -> "Pip" (core panelLabel is "<name>, <words>"). */
export function nameOf(label: string): string {
  const i = label.lastIndexOf(", ");
  return i > 0 ? label.slice(0, i) : label;
}

// ------------------------------------------------------------------ the swap sheet's groups

/** Under a character's own heading the name is said already: "Pip, side view" -> "side view". */
export function candidateLabel(c: Pick<RefCandidate, "label" | "characterId">, inCharacterGroup: boolean): string {
  if (!inCharacterGroup || !c.characterId) return c.label;
  const name = nameOf(c.label);
  return name === c.label ? c.label : c.label.slice(name.length + 2);
}

export type CandidateGroupId = `char:${string}` | "location" | "prop" | "product" | "photo" | "user";
export type CandidateGroup<C extends RefCandidate = RefCandidate> = { id: CandidateGroupId; title: string; items: C[] };

const GROUP_TITLES: Record<Exclude<RefRole, "character">, string> = {
  location: "Locations",
  prop: "Props",
  product: "Product photos",
  photo: "This video's photos",
  user: "From your library",
};
const ORDER: Exclude<RefRole, "character">[] = ["location", "prop", "product", "photo", "user"];
const panelRank = (c: RefCandidate) => (c.panel ? PANEL_ORDER.indexOf(c.panel as (typeof PANEL_ORDER)[number]) : -1) >= 0 ? PANEL_ORDER.indexOf(c.panel as (typeof PANEL_ORDER)[number]) : PANEL_ORDER.length + (c.sheet ? SHEETS.indexOf(c.sheet) : SHEETS.length);

/**
 * What else could go in, grouped for the swap sheet: each character's sheet (in the order the characters first
 * appear; angles, faces, poses, then the whole sheets), then locations, props, product photos, the video's photos
 * and pictures from the library. `first`: a character to put on top (the one being swapped). Empty groups are left out.
 */
export function groupCandidates<C extends RefCandidate>(candidates: readonly C[], first?: string): CandidateGroup<C>[] {
  const chars = new Map<string, CandidateGroup<C>>();
  const rest = new Map<Exclude<RefRole, "character">, C[]>();
  for (const c of candidates) {
    if (c.role === "character" && c.characterId) {
      const id = `char:${c.characterId}` as const;
      const g = chars.get(c.characterId) ?? { id, title: `${nameOf(c.label)}'s sheet`, items: [] };
      g.items.push(c);
      chars.set(c.characterId, g);
    } else {
      const role = c.role === "character" ? "user" : c.role;
      rest.set(role, [...(rest.get(role) ?? []), c]);
    }
  }
  const charGroups = [...chars.entries()].map(([id, g]) => ({ ...g, items: [...g.items].sort((a, b) => panelRank(a) - panelRank(b)), _id: id }));
  if (first) charGroups.sort((a, b) => Number(b._id === first) - Number(a._id === first));
  return [
    ...charGroups.map(({ _id: _x, ...g }) => g),
    ...ORDER.filter((r) => rest.get(r)?.length).map((r) => ({ id: r, title: GROUP_TITLES[r], items: rest.get(r)! })),
  ];
}

// ------------------------------------------------------------------ budget and warnings

/** How many distinct characters have a picture among the picks. */
export const charactersIn = (refs: readonly Pick<RefUse, "characterId">[]) => new Set(refs.filter((r) => r.characterId).map((r) => r.characterId)).size;

/** The fold's summary: "3 of 6 · Nano Banana 2.1", or "Imagen 4 takes none" for a model with no references. */
export function budgetLine(view: Pick<ReferencesView, "picked" | "budget">): string {
  const { max, modelShort } = view.budget;
  if (max <= 0) return `${modelShort || "This model"} takes none`;
  return `${Math.min(view.picked.length, max)} of ${max} · ${modelShort}`;
}

export type RefNotice = NoticeData & { suggest?: RefWarning["suggest"] };

/** The picker's warnings as Notices (never folded). A model suggestion rides along for the "Use <model>" button. */
export function warningNotices(view: Pick<ReferencesView, "warnings" | "budget">): RefNotice[] {
  return view.warnings.map((w) => {
    const tone: NoticeData["tone"] = w.code === "MISSING_PANEL" ? "info" : "warning";
    return { tone, code: w.code, text: w.text || fallbackText(w, view.budget), ...(w.suggest ? { suggest: w.suggest } : {}) };
  });
}
function fallbackText(w: RefWarning, b: ReferencesView["budget"]): string {
  if (w.code === "NO_REFS_MODEL") return `${b.modelShort || "This model"} can't use reference pictures, so the frame is made from the words alone.`;
  if (w.code === "MISSING_PANEL") return "A sheet is missing a picture, so the closest one is used.";
  const fits = w.fits ?? b.maxCharacters ?? b.max;
  return `${b.modelShort || "This model"} keeps ${fits} character${fits === 1 ? "" : "s"} in a picture, so the rest are described in words.`;
}

/** The suggestion's button: "Use Seedream 5 Pro · 21 a picture". */
export const suggestLabel = (s: NonNullable<RefWarning["suggest"]>) => `Use ${s.short} · ${s.credits} a picture`;

/**
 * Before adding a character: the model's character limit (Gemini 2.1 keeps 4). Null when there's room, when the
 * candidate isn't a new character, or when the model has no separate limit.
 */
export function characterLimitNote(view: Pick<ReferencesView, "picked" | "budget">, candidate?: Pick<RefCandidate, "characterId" | "label"> | null): string | null {
  const max = view.budget.maxCharacters;
  if (!max) return null;
  const have = charactersIn(view.picked);
  if (have < max) return null;
  if (candidate && (!candidate.characterId || view.picked.some((r) => r.characterId === candidate.characterId))) return null;
  const who = candidate?.label ? nameOf(candidate.label) : "another character";
  return `${view.budget.modelShort} keeps ${max} characters in a picture. Adding ${who} means one of them is described in words instead.`;
}

/** When every place is used, a new pin pushes the last pick out. */
export function fullNote(view: Pick<ReferencesView, "picked" | "budget">): string | null {
  const { max } = view.budget;
  if (max <= 0 || view.picked.length < max) return null;
  const last = view.picked[view.picked.length - 1];
  return `All ${max} places are used. A new one pushes out the last: ${last?.label ?? "the last picture"}.`;
}

// ------------------------------------------------------------------ taps -> PUT bodies

const without = (list: readonly string[], k: string) => list.filter((x) => x !== k);
const withKey = (list: readonly string[], k: string) => (list.includes(k) ? [...list] : [...list, k]);

/** Pin (or unpin) a pick: pinned pictures go first and stay whatever the words say. */
export function togglePin(o: RefOverrides, key: RefKey): SetReferencesBody {
  return o.pins.includes(key) ? { pins: without(o.pins, key), removed: [...o.removed] } : { pins: withKey(o.pins, key), removed: without(o.removed, key) };
}

/** Take a pick out (it stays out until added back). */
export function removeRef(o: RefOverrides, key: RefKey): SetReferencesBody {
  return { pins: without(o.pins, key), removed: withKey(o.removed, key) };
}

/**
 * Swap a pick for a candidate. Another panel of the same character just pins the new one (a pinned character
 * isn't picked again, and removing the old panel would leave that character with no picture later); anything else
 * takes the old one out and pins the new one.
 */
export function swapRef(o: RefOverrides, from: Pick<RefUse, "assetId" | "characterId" | "panel" | "sheet">, to: Pick<RefCandidate, "key" | "characterId">): SetReferencesBody {
  const fromKey = refKey(from);
  const same = !!from.characterId && from.characterId === to.characterId;
  const pins = withKey(without(o.pins, fromKey), to.key);
  const removed = without(same ? o.removed : withKey(o.removed, fromKey), to.key);
  return { pins, removed };
}

/** Add a candidate (or bring a removed one back): pinned, so it's sent. */
export function addRef(o: RefOverrides, key: RefKey): SetReferencesBody {
  return { pins: withKey(o.pins, key), removed: without(o.removed, key) };
}

/** Back to automatic: no pins, nothing removed. */
export const resetRefs = (): SetReferencesBody => ({ pins: [], removed: [] });
export const hasOverrides = (o: RefOverrides) => o.pins.length > 0 || o.removed.length > 0;

// ------------------------------------------------------------------ changed since the frame

/**
 * What the current frame was made with, as keys. The frame's own `refs` when the api sends them; else what this
 * browser saw when the frame was made (`stored`, see nextBaseline). Null: unknown (no frame, or nothing to compare).
 */
export type Baseline = { frameAssetId: string; keys: RefKey[] } | { pending: true; prevAssetId: string | null; keys: RefKey[] };

export function baselineKeys(frame: { assetId: string; refs?: RefUse[] } | null | undefined, stored: Baseline | null): RefKey[] | null {
  if (!frame) return null;
  if (frame.refs) return frame.refs.map(refKey);
  if (!stored) return null;
  if ("pending" in stored) return stored.prevAssetId !== frame.assetId ? stored.keys : null;
  return stored.frameAssetId === frame.assetId ? stored.keys : null;
}

/**
 * The baseline to remember next. A new frame adopts a pending one (the picks sent with "Redo with these"); a frame
 * with nothing remembered takes the picks on screen the first time (they're what it was made with, unless the
 * references were changed before this browser saw it).
 */
export function nextBaseline(frame: { assetId: string; refs?: RefUse[] } | null | undefined, stored: Baseline | null, current: RefKey[]): Baseline | null {
  if (!frame || frame.refs) return stored;
  if (stored && "pending" in stored) return stored.prevAssetId !== frame.assetId ? { frameAssetId: frame.assetId, keys: stored.keys } : stored;
  if (stored && stored.frameAssetId === frame.assetId) return stored;
  return { frameAssetId: frame.assetId, keys: current };
}

/** The picks differ from the frame's (order counts: the model reads them in order). Unknown counts as unchanged. */
export function refsChanged(current: readonly RefKey[], base: readonly RefKey[] | null): boolean {
  if (!base) return false;
  return current.length !== base.length || current.some((k, i) => k !== base[i]);
}

/** "Redo with these · 8" (the price is always on the button: it's paid). */
export const redoLabel = (credits: number | null) => (credits == null ? "Redo with these" : `Redo with these · ${credits}`);

// ------------------------------------------------------------------ uploads

/** v4 Q5: pictures up to 20 MB, PNG/JPEG/WebP (V4-A2's DEFAULT_LIMITS.image; the api checks again). */
export const IMAGE_MAX_BYTES = DEFAULT_LIMITS.image.maxBytes;
export const IMAGE_TYPES = DEFAULT_LIMITS.image.mimes;
export function imageProblem(file: Pick<File, "type" | "size">): string | null {
  if (!IMAGE_TYPES.includes(file.type)) return "Pick a PNG, JPEG or WebP picture.";
  if (file.size > IMAGE_MAX_BYTES) return "That picture is over 20 MB.";
  return null;
}
